import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { LOGO_MARK } from '../../lib/brand';
import { usePortalTheme } from '../PortalThemeContext';

const SUPPORTING_DELAY_MS = 2000;
const TIMEOUT_MS = 10000;

/**
 * Branded startup splash — replaces indefinite blank loading. Shows the
 * logo immediately, a labeled progress line after 2s, and a retryable
 * connection error if nothing has resolved after a longer timeout.
 * Has its own light/dark look driven by the portal's theme toggle: dark
 * mode keeps the moody photo backdrop, light mode swaps to a clean brand-
 * tinted surface so the two never clash mid-launch.
 */
export const PortalSplash = ({ logoUrl, name, testId = 'portal-splash' }) => {
  const [stage, setStage] = useState('initial');
  const { theme } = usePortalTheme();
  useEffect(() => {
    const t1 = setTimeout(() => setStage((s) => (s === 'initial' ? 'supporting' : s)), SUPPORTING_DELAY_MS);
    const t2 = setTimeout(() => setStage('timeout'), TIMEOUT_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className={`portal-splash portal-splash-${theme}`} role="status" aria-live="polite" data-testid={testId}
      style={theme === 'dark' ? { backgroundImage: `url(${process.env.PUBLIC_URL}/portal-splash.jpg)` } : undefined}>
      <div className="portal-splash-mark">
        <img src={logoUrl || LOGO_MARK} alt="" className="portal-splash-logo" />
      </div>
      <p className="portal-splash-wordmark" data-testid="portal-splash-wordmark">Duti<em>Lyne</em></p>
      <p className="portal-splash-kicker">Pharmacy POS</p>
      <p className="portal-splash-tagline" data-testid="portal-splash-tagline">
        Closer to your patients. Connected to your pharmacy.
      </p>
      {name && <span className="sr-only">{name}</span>}
      {stage !== 'timeout' ? <>
        <Loader2 className="portal-splash-spinner animate-spin" aria-hidden="true" size={22} />
        {stage === 'supporting' && <p className="portal-splash-text" data-testid="portal-splash-progress">Opening your patient portal…</p>}
      </> : <div className="portal-splash-error" data-testid="portal-splash-timeout">
        <p className="portal-splash-text">We’re having trouble connecting</p>
        <button type="button" className="portal-splash-retry" data-testid="portal-splash-retry" onClick={() => window.location.reload()}>
          <RefreshCw size={15} aria-hidden="true" /> Retry
        </button>
      </div>}
    </div>
  );
};
